import { useState } from 'react';
import { Link } from 'react-router-dom';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import { usePopularNews, useCategories } from '@/hooks/useNews';
import { useReadingList } from '@/hooks/useReadingList';
import { useSEO } from '@/hooks/useSEO';

function newsHref(item: { id: string; slug?: string }): string {
  return item.slug ? `/haber/${item.slug}` : `/haber/${item.id}`;
}

// ... existing code ...

export default function PopularNewsPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const { isSaved, toggleSave } = useReadingList();
  const { news: popularNews } = usePopularNews(50);
  const { categories } = useCategories();

  const filtered = activeCategory === 'all'
    ? popularNews
    : popularNews.filter(n => n.category === activeCategory);

  const top3 = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  useSEO({
    title: 'Popüler Teknoloji Haberleri - En Çok Okunanlar | TeknoNovaHub',
    description: 'TeknoNovaHub\'da en çok okunan ve görüntülenen teknoloji haberleri. Yapay zeka, yazılım, girişimcilik alanlarında trend haberler.',
    keywords: 'popüler haberler, en çok okunan, trend teknoloji haberleri, TeknoNovaHub',
    url: '/populer',
    type: 'website',
  });

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />
      <main className="pt-28 md:pt-32">
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <nav className="flex items-center gap-2 text-sm text-white/40 mb-4">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">Popüler Haberler</span>
          </nav>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B6B]/10 rounded-xl">
              <i className="ri-fire-line text-[#FF6B6B] text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">Popüler Haberler</h1>
              <p className="text-white/50 text-sm">Görüntülenme sayısına göre sıralanmış</p>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 mb-10">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${activeCategory === 'all' ? 'bg-white text-[#0A0E27]' : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'}`}
            >
              Tümü
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'text-[#0A0E27] font-bold' : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'}`}
                style={activeCategory === cat.id ? { backgroundColor: cat.color } : {}}
              >
                <i className={`${cat.icon} text-sm`} />{cat.name}
              </button>
            ))}
          </div>
        </section>

        {top3.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {top3.map((item, index) => {
                const cat = categories.find(c => c.id === item.category);
                const saved = isSaved(item.id);
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <div key={item.id} className="relative group">
                    <Link to={newsHref(item)} className="block cursor-pointer">
                      <div className="relative overflow-hidden rounded-2xl">
                        <div className="aspect-[4/3]">
                          <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-[#0A0E27]/40 to-transparent" />
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="text-2xl">{medals[index]}</span>
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ backgroundColor: `${cat?.color}25`, color: cat?.color }}>{cat?.name}</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-white font-bold text-base line-clamp-2 mb-2">{item.title}</h3>
                          <div className="flex items-center gap-3 text-white/50 text-xs">
                            <div className="flex items-center gap-1">
                              <i className="ri-eye-line" />
                              <span className="font-semibold text-[#00D9FF]">{item.views.toLocaleString()}</span>
                              <span>görüntüleme</span>
                            </div>
                            <span>·</span>
                            <span>{item.read_time} okuma</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    <button
                      onClick={() => toggleSave(item.id)}
                      className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer z-10 ${saved ? 'bg-[#00D9FF] text-[#0A0E27]' : 'bg-[#0A0E27]/70 text-white/60 hover:bg-[#0A0E27] hover:text-white backdrop-blur-sm'}`}
                    >
                      <i className={`${saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {rest.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 pb-16">
            <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <i className="ri-list-ordered text-[#00D9FF]" />Diğer Popüler Haberler
            </h2>
            <div className="flex flex-col gap-3">
              {rest.map((item, index) => {
                const cat = categories.find(c => c.id === item.category);
                const saved = isSaved(item.id);
                return (
                  <div key={item.id} className="group flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/8 transition-all">
                    <span className="text-white/30 font-black text-xl w-8 text-center flex-shrink-0">{index + 4}</span>
                    <Link to={newsHref(item)} className="flex-shrink-0 cursor-pointer">
                      <div className="w-20 h-14 rounded-lg overflow-hidden">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    </Link>
                    <Link to={newsHref(item)} className="flex-1 min-w-0 cursor-pointer">
                      <span className="text-[10px] font-bold uppercase tracking-wide mb-1 block" style={{ color: cat?.color }}>{cat?.name}</span>
                      <h3 className="text-white font-semibold text-sm line-clamp-2 group-hover:text-[#00D9FF] transition-colors">{item.title}</h3>
                      <div className="flex items-center gap-3 text-white/40 text-xs mt-1">
                        <div className="flex items-center gap-1"><i className="ri-eye-line" /><span className="text-[#00D9FF] font-semibold">{item.views.toLocaleString()}</span></div>
                        <span>·</span><span>{item.date}</span><span>·</span><span>{item.read_time} okuma</span>
                      </div>
                    </Link>
                    <button
                      onClick={() => toggleSave(item.id)}
                      className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${saved ? 'bg-[#00D9FF] text-[#0A0E27]' : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white'}`}
                    >
                      <i className={`${saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <NewsFooter />
    </div>
  );
}
